import { useCallback } from "react"
import { LoadingOverlay } from "@mantine/core"
import {
  CreateReminderRequest,
  EditReminderRequest,
  useEditReminder,
  useDeleteReminder,
} from "@/orval/gen"
import { ReminderDetailDrawerProps } from "../reminder-detail-drawer"
import { useForm } from "@mantine/form"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { CreateOrEditReminderForm } from "./CreateOrEditReminderForm"
import { invalidateReminderQueries } from "../invalidate-reminder-queries"

type ReminderFormValues = CreateReminderRequest | EditReminderRequest

export function EditReminderDrawer({
  opened,
  onClose,
  reminder,
}: ReminderDetailDrawerProps) {
  const queryClient = useQueryClient()
  const { mutateAsync: updateReminder, isPending } = useEditReminder({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        invalidateReminderQueries(queryClient)
        onClose()
      },
    },
  })
  const [isDeleting, setIsDeleting] = React.useState(false)
  const { mutateAsync: deleteReminder } = useDeleteReminder({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        invalidateReminderQueries(queryClient)
        setIsDeleting(false)
        onClose()
      },
    },
  })
  const handleDelete = useCallback(async () => {
    if (!reminder?.reminderId) return
    setIsDeleting(true)
    await deleteReminder({ data: { reminderId: reminder.reminderId } })
  }, [reminder, deleteReminder])
  const form = useForm<ReminderFormValues>({ mode: "uncontrolled" })
  const resetForm = useCallback(() => {
    form.setValues({
      ...reminder,
    })
  }, [reminder])

  React.useEffect(() => {
    if (opened) {
      resetForm()
    }
  }, [opened, resetForm])

  const handleSubmit = useCallback(
    async (formValues: ReminderFormValues) => {
      const editRequest = formValues as EditReminderRequest
      await updateReminder({ data: editRequest })
    },
    [updateReminder]
  )

  return (
    <div style={{ position: "relative" }}>
      <LoadingOverlay visible={isPending || isDeleting} />
      <CreateOrEditReminderForm
        form={form}
        reminder={reminder}
        handleSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isPending}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  )
}
